import type { TripTagImage } from "@przezmorze/api-types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import sortBy from "lodash/sortBy";
import { useSnackbar } from "notistack";
import type { DeleteTripTagResponse, TripTag, TripTagCategory } from "./tripTagsApi";
import { addTripTag, deleteTripTag, listTripTags, updateTripTag } from "./tripTagsApi";

const orderTagsKey = "order-tags-dictionary";

export const useTripTags = () => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: [orderTagsKey],
    queryFn: ({ signal }) => listTripTags(signal),
  });

  const addMutation = useMutation<
    TripTag,
    unknown,
    { category: TripTagCategory; name: string; isPopular: boolean; tagImage?: TripTagImage }
  >({
    mutationFn: ({ category, name, isPopular, tagImage }) => addTripTag(category, name, isPopular, tagImage),
    onSuccess: async (data) => {
      enqueueSnackbar(`Tag '${data.name}' w kategorii '${data.category}' został dodany do słownika`, {
        variant: "success",
      });
      await queryClient.invalidateQueries({ queryKey: [orderTagsKey] });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się dodać taga do słownika. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  const updateMutation = useMutation<
    TripTag,
    unknown,
    {
      readonly id: string;
      readonly category: TripTagCategory;
      readonly name: string;
      readonly isPopular: boolean;
      readonly tagImage?: TripTagImage;
    }
  >({
    mutationFn: ({ id, category, name, isPopular, tagImage }) => updateTripTag(id, category, name, isPopular, tagImage),
    onSuccess: async ({ name }) => {
      enqueueSnackbar(`Tag został zaktualizowany: ${name}`, {
        variant: "success",
      });
      await queryClient.invalidateQueries({ queryKey: [orderTagsKey] });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się zaktualizować taga. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  const deleteMutation = useMutation<DeleteTripTagResponse, unknown, string>({
    mutationFn: (id) => deleteTripTag(id),
    onSuccess: async ({ updatedOrders, updatedPackages }) => {
      enqueueSnackbar(
        `Tag został usunięty ze słownika. Zaktualizowano ${updatedOrders} zamówień i ${updatedPackages} pakietów`,
        {
          variant: "success",
        },
      );
      await queryClient.invalidateQueries({ queryKey: [orderTagsKey] });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się usunąć taga ze słownika. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  return {
    tags: sortBy(
      data ?? [],
      ({ category }) => category.trim().toLowerCase(),
      ({ name }) => name.trim().toLowerCase(),
    ),
    isLoading,
    addTag: (category: TripTagCategory, name: string, isPopular: boolean, tagImage?: TripTagImage) =>
      addMutation.mutateAsync({ category, name, isPopular, tagImage }),
    updateTag: (id: string, category: TripTagCategory, name: string, isPopular: boolean, tagImage?: TripTagImage) =>
      updateMutation.mutateAsync({ id, category, name, isPopular, tagImage }),
    deleteTag: (id: string) => deleteMutation.mutateAsync(id),
  };
};
